import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import {
  BRANDING_FILE_RULES,
  salonBrandingApi,
  validateBrandingFile,
} from '../utils/salonBrandingApi';

const EMPTY_BRANDING = { logo: null, cover: null };
const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp';

const useObjectUrl = (file) => {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!file || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
      setUrl('');
      return undefined;
    }
    const nextUrl = URL.createObjectURL(file);
    setUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [file]);
  return url;
};

const ImageActions = ({
  kind,
  label,
  file,
  hasPublishedImage,
  markedForRemoval,
  onChoose,
  onRemove,
  onUndoRemove,
  disabled,
}) => {
  const rules = BRANDING_FILE_RULES[kind];
  return (
    <Stack spacing={1.25}>
      <Box>
        <Typography fontWeight={900}>{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          JPG, PNG ou WebP • até {rules.maxLabel} • mínimo {rules.minDimensions}
        </Typography>
      </Box>
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Button
          component="label"
          variant="outlined"
          startIcon={<PhotoCameraOutlinedIcon />}
          disabled={disabled}
          aria-label={`${file || hasPublishedImage ? 'Substituir' : 'Selecionar'} ${label.toLowerCase()}`}
        >
          {file || hasPublishedImage ? 'Substituir imagem' : 'Selecionar imagem'}
          <input
            hidden
            type="file"
            accept={IMAGE_ACCEPT}
            onChange={(event) => {
              onChoose(event.target.files?.[0] || null);
              event.target.value = '';
            }}
          />
        </Button>
        {(file || hasPublishedImage) && !markedForRemoval ? (
          <Button
            color="error"
            variant="text"
            startIcon={<DeleteOutlineIcon />}
            onClick={onRemove}
            disabled={disabled}
            aria-label={`Remover ${label.toLowerCase()}`}
          >
            Remover
          </Button>
        ) : null}
        {markedForRemoval ? (
          <Button
            variant="text"
            onClick={onUndoRemove}
            disabled={disabled}
            aria-label={`Desfazer remoção de ${label.toLowerCase()}`}
          >
            Desfazer remoção
          </Button>
        ) : null}
      </Stack>
      {markedForRemoval ? (
        <Typography variant="caption" color="error.main">
          A imagem será removida quando você salvar.
        </Typography>
      ) : file ? (
        <Typography variant="caption" color="success.main" sx={{ overflowWrap: 'anywhere' }}>
          Nova imagem pronta para salvar: {file.name}
        </Typography>
      ) : null}
    </Stack>
  );
};

const SalonBrandingDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [branding, setBranding] = useState(EMPTY_BRANDING);
  const [logoFile, setLogoFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [removeCover, setRemoveCover] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const logoObjectUrl = useObjectUrl(logoFile);
  const coverObjectUrl = useObjectUrl(coverFile);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    setSuccess('');
    setLogoFile(null);
    setCoverFile(null);
    setRemoveLogo(false);
    setRemoveCover(false);
    salonBrandingApi.get()
      .then((data) => {
        if (active) setBranding(data || EMPTY_BRANDING);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message || 'Falha ao carregar a identidade da loja.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [open]);

  const logoPreview = logoObjectUrl || (!removeLogo ? branding.logo?.url : '');
  const coverPreview = coverObjectUrl || (!removeCover ? branding.cover?.url : '');
  const hasChanges = Boolean(logoFile || coverFile || removeLogo || removeCover);

  const previewLabel = useMemo(() => (
    coverPreview || logoPreview ? 'Prévia da identidade publicada no cardápio' : 'Prévia do visual padrão'
  ), [coverPreview, logoPreview]);

  const chooseFile = (kind, file) => {
    if (!file) return;
    const validationError = validateBrandingFile(kind, file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setSuccess('');
    if (kind === 'logo') {
      setLogoFile(file);
      setRemoveLogo(false);
    } else {
      setCoverFile(file);
      setRemoveCover(false);
    }
  };

  const removeImage = (kind) => {
    setError('');
    setSuccess('');
    if (kind === 'logo') {
      setLogoFile(null);
      setRemoveLogo(Boolean(branding.logo));
    } else {
      setCoverFile(null);
      setRemoveCover(Boolean(branding.cover));
    }
  };

  const save = async () => {
    if (!hasChanges) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const saved = await salonBrandingApi.save({
        logoFile,
        coverFile,
        removeLogo,
        removeCover,
      });
      setBranding(saved || EMPTY_BRANDING);
      setLogoFile(null);
      setCoverFile(null);
      setRemoveLogo(false);
      setRemoveCover(false);
      setSuccess('Identidade do cardápio atualizada com sucesso.');
    } catch (requestError) {
      setError(requestError.message || 'Falha ao salvar a identidade da loja.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle sx={{ pr: 7 }}>
        <Typography component="span" variant="h6" fontWeight={950}>
          Identidade do cardápio QR
        </Typography>
        <IconButton
          aria-label="Fechar configuração de identidade"
          onClick={onClose}
          disabled={saving}
          sx={{ position: 'absolute', top: 10, right: 10 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack minHeight={320} alignItems="center" justifyContent="center" spacing={1.5}>
            <CircularProgress />
            <Typography color="text.secondary">Carregando identidade...</Typography>
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Box>
              <Typography color="text.secondary">
                Personalize a tela aberta pelo QR das mesas. As imagens são otimizadas automaticamente antes de serem publicadas.
              </Typography>
            </Box>

            {error ? <Alert severity="error">{error}</Alert> : null}
            {success ? <Alert severity="success">{success}</Alert> : null}

            <Box
              aria-label={previewLabel}
              sx={{
                position: 'relative',
                aspectRatio: '16 / 9',
                minHeight: { xs: 190, sm: 260 },
                maxHeight: 390,
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#111827',
                backgroundImage: coverPreview
                  ? `linear-gradient(180deg, rgba(15,23,42,0.05), rgba(15,23,42,0.7)), url("${coverPreview}")`
                  : 'linear-gradient(135deg, #1f2937, #0f172a)',
                backgroundPosition: 'center',
                backgroundSize: 'cover',
              }}
            >
              {!coverPreview ? (
                <Stack
                  alignItems="center"
                  justifyContent={{ xs: 'flex-start', sm: 'center' }}
                  height="100%"
                  color="rgba(255,255,255,0.68)"
                  spacing={1}
                  sx={{ pt: { xs: 3.5, sm: 0 }, pb: { xs: 11, sm: 0 } }}
                >
                  <ImageOutlinedIcon sx={{ fontSize: 44 }} />
                  <Typography variant="body2">A capa aparecerá aqui</Typography>
                </Stack>
              ) : null}
              <Stack
                direction="row"
                alignItems="center"
                gap={1.5}
                sx={{ position: 'absolute', left: { xs: 16, sm: 24 }, right: 16, bottom: { xs: 16, sm: 22 } }}
              >
                <Avatar
                  src={logoPreview || undefined}
                  alt={logoPreview ? 'Prévia da logo da loja' : ''}
                  sx={{
                    width: { xs: 64, sm: 82 },
                    height: { xs: 64, sm: 82 },
                    bgcolor: '#fff',
                    color: 'primary.main',
                    border: '3px solid #fff',
                    boxShadow: '0 8px 24px rgba(15,23,42,0.3)',
                    '& img': { objectFit: 'contain' },
                  }}
                >
                  {!logoPreview ? <StorefrontOutlinedIcon fontSize="large" /> : null}
                </Avatar>
                <Box minWidth={0}>
                  <Typography color="#fff" fontWeight={950} variant={fullScreen ? 'h6' : 'h5'} noWrap>
                    Sua loja
                  </Typography>
                  <Typography color="rgba(255,255,255,0.82)" variant="body2">
                    Cardápio da mesa
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3,
              }}
            >
              <ImageActions
                kind="logo"
                label="Logo da loja"
                file={logoFile}
                hasPublishedImage={Boolean(branding.logo)}
                markedForRemoval={removeLogo}
                onChoose={(file) => chooseFile('logo', file)}
                onRemove={() => removeImage('logo')}
                onUndoRemove={() => setRemoveLogo(false)}
                disabled={saving}
              />
              <Divider sx={{ display: { xs: 'block', md: 'none' } }} />
              <ImageActions
                kind="cover"
                label="Capa da loja"
                file={coverFile}
                hasPublishedImage={Boolean(branding.cover)}
                markedForRemoval={removeCover}
                onChoose={(file) => chooseFile('cover', file)}
                onRemove={() => removeImage('cover')}
                onUndoRemove={() => setRemoveCover(false)}
                disabled={saving}
              />
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
        <Button onClick={onClose} disabled={saving} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          Fechar
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={loading || saving || !hasChanges}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
          aria-label={saving ? 'Salvando identidade' : 'Salvar identidade'}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {saving ? 'Salvando identidade' : 'Salvar identidade'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalonBrandingDialog;
